/**
 * F2 — Metadata retrieve + compare Zod schemas.
 */
import { z } from 'zod';
// ── Default metadata type set ─────────────────────────────────────────────────
// Curated working set; callers can narrow or widen this list.
// Kept as a plain array (not `as const`) so it's extensible at runtime.
export const DEFAULT_METADATA_TYPES = [
    'ApexClass',
    'ApexTrigger',
    'ApexPage',
    'CustomObject',
    'CustomField',
    'FieldSet',
    'ValidationRule',
    'RecordType',
    'Layout',
    'Flow',
    'PermissionSet',
    'CustomLabel',
    // 'StaticResource' — NOT selectable, on purpose (see below).
    'CustomTab',
    'CustomApplication',
    // OmniStudio. Ordinary Metadata API types that deploy exactly like ApexClass — they were missing from this
    // list, so they never appeared in a compare and could never be selected into a set. An org without the
    // OmniStudio package does not error on them: the orchestrator drops a type the org's describe does not
    // report, but only for THIS default set (an explicitly requested type still errors rather than quietly
    // comparing nothing).
    'OmniScript',
    'OmniIntegrationProcedure',
    'OmniDataTransform',
    'OmniUiCard',
    // Access + org configuration. Every path verified against the CLI registry (metadataTypeTable.ts) rather
    // than derived — deriving is what made StaticResource and OmniScript undeployable.
    //
    // ── Why StaticResource is not in this list ──
    // Its native layout is TWO files: `staticresources/<name>.resource-meta.xml` PLUS a content file whose
    // extension varies (.js, .css, .zip …) — the registry calls it `mixedContent`. Stride's snapshot stores
    // ONE body per component, so it can produce the meta xml and not the content, and a StaticResource
    // without its content does not deploy. Offering it meant an operator could pick it and get a parked
    // `.txt` that silently never deployed.
    //
    // So it is refused rather than parked, which is the same choice as the husk refusal (EMPTY_COMPONENT_BODY):
    // fail at authoring, where it is cheap and visible, not at deploy or at runtime.
    //
    // This is INTERIM. The fix is to carry a content file alongside the meta body in the snapshot, after
    // which StaticResource and Document both land at their registry paths and this line comes back.
    'Profile',
    'PermissionSetGroup',
    'Queue',
    'Group',
    'Role',
    'CustomPermission',
    'QuickAction',
    'GlobalValueSet',
    'CustomMetadata',
    'ApprovalProcess',
    'DuplicateRule',
    'NamedCredential',
    'RemoteSiteSetting',
    'ConnectedApp',
    'PathAssistant',
    'LightningMessageChannel',
    'CustomNotificationType',
    'FlexiPage',
    'ApexComponent',
    // Object children beyond fields / validation rules / record types.
    'ListView',
    'CompactLayout',
    'WebLink',
    'BusinessProcess',
];
// ── Core schemas ──────────────────────────────────────────────────────────────
export const componentChangeSchema = z.enum(['added', 'changed', 'deleted', 'unchanged']);
export const metadataComponentSchema = z.object({
    type: z.string(),
    fullName: z.string(),
    hash: z.string(),
});
export const metadataSnapshotSchema = z.object({
    id: z.string().uuid(),
    sourceKind: z.enum(['org', 'branch']),
    sourceRef: z.string().min(1),
    types: z.array(z.string()),
    componentCount: z.number().int().nonnegative(),
    blobPath: z.string(),
    createdAt: z.string().datetime(),
});
export const componentDiffSchema = z.object({
    type: z.string(),
    fullName: z.string(),
    presentIn: z.enum(['both', 'left', 'right']),
    change: componentChangeSchema,
});
export const comparisonSideSchema = z.object({
    kind: z.enum(['org', 'branch']),
    ref: z.string(),
    snapshotId: z.string(),
});
export const comparisonSummarySchema = z.object({
    added: z.number().int().nonnegative(),
    changed: z.number().int().nonnegative(),
    deleted: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
});
export const metadataComparisonSchema = z.object({
    id: z.string().uuid(),
    left: comparisonSideSchema,
    right: comparisonSideSchema,
    summary: comparisonSummarySchema,
    diffs: z.array(componentDiffSchema),
    createdAt: z.string().datetime(),
});
// ── Selector schema ───────────────────────────────────────────────────────────
/**
 * Zod schema for MetadataSelector.
 * - `members` absent / undefined → wildcard (all components of this type).
 * - `members` present → retrieve / compare only those named components.
 */
export const metadataSelectorSchema = z.object({
    type: z.string().min(1),
    members: z.array(z.string()).optional(),
});
// ── ComponentListing schemas ──────────────────────────────────────────────────
export const componentMemberSchema = z.object({
    fullName: z.string(),
    lastModifiedAt: z.string().optional(),
    lastModifiedBy: z.string().optional(),
});
export const componentListingSchema = z.object({
    type: z.string(),
    members: z.array(componentMemberSchema),
});
// ── Request schemas ───────────────────────────────────────────────────────────
/**
 * POST /api/metadata/retrieve
 *
 * Accepts `selectors` (preferred, supports named members) or `types` (legacy, wildcard only).
 * At least one of `types` or `selectors` must be provided.
 */
export const retrieveRequestSchema = z.object({
    connectionId: z.string().min(1),
    types: z.array(z.string()).min(1).optional(),
    selectors: z.array(metadataSelectorSchema).min(1).optional(),
}).refine((d) => d.types !== undefined || d.selectors !== undefined, { message: 'Either types or selectors must be provided' });
export const compareSourceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('org'), connectionId: z.string().min(1) }),
    z.object({ kind: z.literal('branch'), connectionId: z.string().min(1), repo: z.string().min(1), branch: z.string().min(1) }),
    z.object({ kind: z.literal('snapshot'), snapshotId: z.string().min(1) }),
]);
/**
 * POST /api/metadata/compare
 *
 * `selectors` (preferred) or `types` (legacy).  When both are absent,
 * the server uses DEFAULT_METADATA_TYPES as a wildcard fallback.
 */
export const compareRequestSchema = z.object({
    left: compareSourceSchema,
    right: compareSourceSchema,
    types: z.array(z.string()).optional(), // legacy — wildcard per type
    selectors: z.array(metadataSelectorSchema).optional(), // preferred — supports named members
});
/** POST /api/metadata/list  — cheap names-only listing (no bodies). */
export const listMetadataRequestSchema = z.object({
    connectionId: z.string().min(1),
    types: z.array(z.string()).min(1),
});
export const refreshTypeRequestSchema = z.object({
    type: z.string().min(1),
});
/** A metadata component identity (mirrors the web SelectedComponent). */
export const componentRefSchema = z.object({
    type: z.string().min(1),
    fullName: z.string().min(1),
});
/**
 * POST /api/metadata/dependencies — direct dependencies of a selection (one hop
 * per component, unioned). `connectionId` is the SOURCE org whose reference graph
 * (MetadataComponentDependency) is queried. The ARM metadata scanner reads source
 * component bodies to resolve dependencies MCD doesn't cover; the source snapshot
 * is supplied either directly via `snapshotId` or — the usual path from the compare
 * UI, which holds only the comparison id — via `comparisonId` (the route resolves
 * it to the comparison's source/left snapshotId). Both optional; `snapshotId` wins.
 */
export const metadataDependenciesRequestSchema = z.object({
    connectionId: z.string().min(1),
    selection: z.array(componentRefSchema).min(1),
    snapshotId: z.string().min(1).optional(),
    comparisonId: z.string().min(1).optional(),
});
/** POST /api/metadata/dependencies/neighbors — one-hop deps of a single component. */
export const metadataNeighborsRequestSchema = z.object({
    connectionId: z.string().min(1),
    component: componentRefSchema,
    snapshotId: z.string().min(1).optional(),
    comparisonId: z.string().min(1).optional(),
});
// ── MetadataTypeInfo schema ──────────────────────────────────────────────────
export const metadataTypeInfoSchema = z.object({
    xmlName: z.string(),
    directoryName: z.string(),
    inFolder: z.boolean(),
    childXmlNames: z.array(z.string()).optional(),
    category: z.string(),
});
/** GET /api/metadata/types?connectionId= */
export const describeTypesRequestSchema = z.object({
    connectionId: z.string().min(1),
});
//# sourceMappingURL=metadata.js.map